import { Controller } from "@hotwired/stimulus"
import { ALLOWED_COUNTRY_CODES } from "@appConfig/allowed-countries"
import { MAPBOX_TOKEN } from "@appConfig/api-keys"
import { zIndexAboveOpenModals } from "../src/shared/helpers/modal_stacking"

const ICONS = {
  fa: {
    suggestion: "fa fa-location-dot",
    noResults: "fa fa-circle-info"
  },
  ph: {
    suggestion: "ph ph-map-pin",
    noResults: "ph ph-info"
  }
}

export default class extends Controller {
  static targets = [
    "input",
    "suggestions",
    "longitude", "latitude", "city", "country", "postalCode", "streetNumber", "streetName", "displayName", "approximate", "destroy"
  ]

  static values = {
    onlyAddresses: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    noResults: String,
    // Optional endpoint returning extra <li> suggestions (HTML) for the query `q`,
    // listed after the places — e.g. practitioner names in the visitor search bar.
    extraSuggestionsUrl: String,
    // Optional "lng,lat": Mapbox lists places near this point first (e.g. the zone of the page).
    proximity: String,
    iconSet: { type: String, default: "fa" }
  }

  connect () {
    this.suggestionsList = this.element.querySelector('[data-address-autocomplete-target="suggestions"]')
    this.inModal = Boolean(this.element.closest(".modal"))
    this.suggestionsParent = this.suggestionsList?.parentElement

    this.inputTarget.value = this.displayNameTarget.value || ""
    this.suggestions = []
    this.extraSuggestionsHtml = ""
    this.isSearching = false
    this.debounceTimer = null
    this.address = {
      longitude: this.longitudeTarget.value,
      latitude: this.latitudeTarget.value,
      city: this.cityTarget.value,
      country: this.countryTarget.value,
      postal_code: this.postalCodeTarget.value,
      street_number: this.streetNumberTarget.value,
      street_name: this.streetNameTarget.value,
      display_name: this.displayNameTarget.value,
      ...(this.hasApproximateTarget && { approximate: this.approximateTarget.value }),
      destroy: this.destroyTarget.value
    }

    if (this.inModal) {
      this.onModalScroll = () => this.positionSuggestions()
      this.element.closest(".modal")?.addEventListener("scroll", this.onModalScroll, true)
    }
  }

  disconnect () {
    if (this.onModalScroll) {
      this.element.closest(".modal")?.removeEventListener("scroll", this.onModalScroll, true)
    }

    if (this.inModal && this.suggestionsList?.parentElement === document.body) {
      this.suggestionsList.remove()
    }
  }

  onInput () {
    if (this.disabledValue) return

    clearTimeout(this.debounceTimer)

    if (!this.inputTarget.value.trim()) {
      this.suggestions = []
      this.extraSuggestionsHtml = ""
      this.isSearching = false
      this.renderSuggestions()
      return
    }

    this.isSearching = true
    this.debounceTimer = setTimeout(() => this.fetchSuggestions(), 300)
  }

  placesUrl (query) {
    const countriesParam = ALLOWED_COUNTRY_CODES.join(',')
    const encodedQuery = encodeURIComponent(query)
    const typesParam = this.onlyAddressesValue ? "&types=address" : ""
    const proximityParam = this.proximityValue ? `&proximity=${encodeURIComponent(this.proximityValue)}` : ""

    return `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedQuery}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&country=${countriesParam}${typesParam}${proximityParam}`
  }

  fetchSuggestions () {
    const query = this.inputTarget.value

    Promise.all([this.fetchPlaces(query), this.fetchExtraSuggestions(query)])
      .then(([places, extraSuggestionsHtml]) => {
        // A slower response for an older query must not overwrite the current one.
        if (query !== this.inputTarget.value) return

        this.suggestions = places
        this.extraSuggestionsHtml = extraSuggestionsHtml
        this.renderSuggestions()
      })
  }

  fetchPlaces (query) {
    return fetch(this.placesUrl(query))
      .then(res => res.json())
      .then(data => data.features || [])
      .catch(err => {
        console.error("Error fetching suggestions:", err)
        return []
      })
  }

  fetchExtraSuggestions (query) {
    if (!this.extraSuggestionsUrlValue) return Promise.resolve("")

    const url = new URL(this.extraSuggestionsUrlValue, window.location.origin)
    url.searchParams.set("q", query)

    return fetch(url, { headers: { Accept: "text/html" } })
      .then(res => (res.ok ? res.text() : ""))
      .catch(err => {
        console.error("Error fetching extra suggestions:", err)
        return ""
      })
  }

  // Picks the best match for an address known in advance (e.g. an import).
  // Without a match, the suggestions stay open so the user can pick one.
  async fillFromQuery (query) {
    this.inputTarget.value = query

    try {
      const response = await fetch(this.placesUrl(query))
      const place = (await response.json()).features?.[0]
      if (place) {
        this.inputTarget.value = place.place_name
        this.getAddressData(place)
        return true
      }
    } catch (err) {
      console.error("Error geocoding address:", err)
    }

    this.onInput()
    return false
  }

  selectSuggestion (event) {
    const index = event.currentTarget.dataset.index
    const place = this.suggestions[index]

    this.inputTarget.value = place.place_name
    this.suggestions = []
    this.extraSuggestionsHtml = ""
    this.isSearching = false
    this.renderSuggestions()
    this.getAddressData(place)
  }

  getAddressData (place) {
    const context = place.context || []
    const addressData = {}
    let streetName = ""

    if (place.id.startsWith('postcode')) {
      addressData.postal_code = place.text
    }

    context.forEach(item => {
      if (item.id.startsWith('postcode')) addressData.postal_code = item.text
      if (item.id.startsWith('place')) addressData.city = item.text
      if (item.id.startsWith('country')) addressData.country = item.text
    })

    const placeNameParts = place.place_name.split(', ')
    if (!place.id.startsWith('postcode')) streetName = placeNameParts[0]

    this.address = {
      longitude: place.geometry.coordinates[0],
      latitude: place.geometry.coordinates[1],
      city: addressData.city || placeNameParts[1] || "",
      country: addressData.country || placeNameParts[placeNameParts.length - 1] || "",
      postal_code: addressData.postal_code || "",
      street_number: place.address || "",
      street_name: streetName,
      display_name: place.place_name,
      approximate: placeNameParts[placeNameParts.length - 1] === 'Réunion',
      destroy: false
    }

    this.updateHiddenFields()
  }

  clear (event) {
    event.preventDefault()

    if (this.disabledValue) return

    this.inputTarget.value = ""
    this.isSearching = false

    this.suggestions = []
    this.extraSuggestionsHtml = ""
    this.renderSuggestions()

    this.address = {
      destroy: false
    }
    this.updateHiddenFields()
  }

  renderSuggestions () {
    if (!this.suggestionsList) return

    this.suggestionsList.innerHTML = ""

    if (!this.isSearching || (!this.suggestions.length && !this.inputTarget.value.trim())) {
      this.hideSuggestions()
      return
    }

    this.showSuggestions()

    if (this.suggestions.length === 0 && !this.extraSuggestionsHtml.trim()) {
      const li = document.createElement("li")
      li.classList.add("suggestion", "no-results")
      li.innerHTML = `${this.iconMarkup("noResults")}<span>${this.noResultsValue}</span>`
      this.suggestionsList.appendChild(li)
      return
    }

    this.suggestions.forEach((s, index) => {
      const li = document.createElement("li")
      li.classList.add("suggestion")
      li.dataset.index = index
      li.dataset.testid = "address-autocomplete-suggestion"
      li.innerHTML = `${this.iconMarkup("suggestion")}<span>${s.place_name}</span>`
      li.addEventListener("click", this.selectSuggestion.bind(this))
      this.suggestionsList.appendChild(li)
    })

    // Server-rendered markup from our own endpoint (escaped by the view).
    this.suggestionsList.insertAdjacentHTML("beforeend", this.extraSuggestionsHtml)
  }

  iconMarkup (kind) {
    const set = ICONS[this.iconSetValue] ? this.iconSetValue : "fa"
    const iconClass = ICONS[set][kind]

    return `<i class="${iconClass}" aria-hidden="true"></i>`
  }

  updateHiddenFields () {
    this.longitudeTarget.value = this.address.longitude ?? ""
    this.latitudeTarget.value = this.address.latitude ?? ""
    this.cityTarget.value = this.address.city ?? ""
    this.countryTarget.value = this.address.country ?? ""
    this.postalCodeTarget.value = this.address.postal_code ?? ""
    this.streetNumberTarget.value = this.address.street_number ?? ""
    this.streetNameTarget.value = this.address.street_name ?? ""
    this.displayNameTarget.value = this.address.display_name ?? ""

    if (this.hasApproximateTarget) {
      this.approximateTarget.value = this.address.approximate ?? ""
    }

    this.destroyTarget.value = this.address.destroy ?? ""

    this.element.dispatchEvent(new CustomEvent("address-autocomplete:change", {
      bubbles: true,
      detail: {
        latitude: this.address.latitude,
        longitude: this.address.longitude,
        country: this.address.country
      }
    }))
  }

  showSuggestions () {
    if (!this.suggestionsList) return

    if (this.inModal && this.suggestionsList.parentElement !== document.body) {
      document.body.appendChild(this.suggestionsList)
    }

    this.suggestionsList.classList.toggle("address-autocomplete-suggestions--portaled", this.inModal)
    this.suggestionsList.classList.remove("d-none")
    this.positionSuggestions()
  }

  positionSuggestions () {
    if (!this.suggestionsList || !this.inModal || this.suggestionsList.classList.contains("d-none")) return

    const rect = this.inputTarget.getBoundingClientRect()

    Object.assign(this.suggestionsList.style, {
      position: "fixed",
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: zIndexAboveOpenModals()
    })
  }

  hideSuggestions () {
    if (!this.suggestionsList) return

    this.suggestionsList.classList.add("d-none")
    this.suggestionsList.classList.remove("address-autocomplete-suggestions--portaled")

    if (this.inModal && this.suggestionsParent && this.suggestionsList.parentElement === document.body) {
      this.suggestionsParent.appendChild(this.suggestionsList)
      Object.assign(this.suggestionsList.style, {
        position: "",
        top: "",
        left: "",
        width: "",
        zIndex: ""
      })
    }
  }
}
